import { useEffect, useState } from 'react';
import { useDispatcherVideoContext } from '../../DispatcherVideoContext/useDispatcherVideoContext';

const DEFAULT_DEVICE_ID = undefined;
/**
 * Filters a device ID to ensure it still exists in the browser's available media devices.
 * Returns undefined if the device ID is not found or if there's an error accessing devices.
 */
async function validateDeviceId(
  deviceId: string | undefined,
  kind: MediaDeviceKind,
): Promise<string | undefined> {
  if (!deviceId) return DEFAULT_DEVICE_ID;

  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const deviceExists = devices.some(
      device => device.deviceId === deviceId && device.kind === kind,
    );

    if (deviceExists) {
      return deviceId;
    }

    console.warn(
      `Preferred ${kind} device ${deviceId} not found in available devices. Resorting to system default.`,
    );
    return DEFAULT_DEVICE_ID;
  } catch (error) {
    console.warn('Failed to enumerate media devices:', error);
    return deviceId;
  }
}

/**
 * Hook to manage validated audio device IDs
 */
export function usePreferredAudioDevices() {
  const { getPreferredmediaDeviceId, setPreferredMediaDeviceId } =
    useDispatcherVideoContext();

  // Get the current preferred device IDs on each render
  const audioInputIdSaved = getPreferredmediaDeviceId('audioinput');
  const audioOutputIdSaved = getPreferredmediaDeviceId('audiooutput');

  const [audioInputIdValidated, setAudioInputIdValidated] = useState<
    string | undefined
  >(audioInputIdSaved);
  const [audioOutputIdValidated, setAudioOutputIdValidated] = useState<
    string | undefined
  >(audioOutputIdSaved);

  // validate device IDs only when the actual device IDs change to avoid enumeration of devices
  useEffect(() => {
    const validateDevices = async () => {
      const [validInput, validOutput] = await Promise.all([
        validateDeviceId(audioInputIdSaved, 'audioinput'),
        validateDeviceId(audioOutputIdSaved, 'audiooutput'),
      ]);

      setAudioInputIdValidated(validInput);
      setAudioOutputIdValidated(validOutput);
    };

    validateDevices().catch(error => {
      console.error('Error validating media devices:', error);
    });
  }, [audioInputIdSaved, audioOutputIdSaved]);

  return {
    preferredAudioInputDeviceId: audioInputIdValidated,
    preferredAudioOutputDeviceId: audioOutputIdValidated,
    setPreferredMediaDeviceId,
  };
}
